import React from 'react';
import { Alert, Space, Tag, Tooltip, Typography } from 'antd';

import {
  AnswerKeyCheck,
  AnswerKeyGate,
  BLOCKED_CONSEQUENCE,
  INVISIBLE_INSTRUMENTS_HEADLINE,
  RETIRED_HEADLINE,
  RETIRED_TAG,
  STALE_NOTE,
  UNRATED_LABEL,
  UNRECORDED_CONSEQUENCE,
  checkLabel,
  gateConsequence,
  gateHeadline,
  gateShortLabel,
} from './gate';

/**
 * One banner, rendered wherever a score or a launchable scenario is shown: the
 * trainer's session report, the admin's session results, the launch modal and
 * the attach picker.
 *
 * The rule it enforces is a product rule, not a styling one. A score produced
 * by an answer key that failed its own quality audit may still be shown -- a
 * LOW score against a weak key is real information -- but it may not wear a
 * hiring band, because the failing check is precisely the finding that a
 * candidate who expresses no view can pass. So: the failing check's own
 * sentence, the consequence in plain English, and the rating band replaced by
 * "Indicative only — not rated".
 *
 * A passing answer key renders nothing at all. The banner has to stay rare to
 * stay read.
 *
 * `data-answer-key-gate` carries the state on the root element. It is there for
 * the end-to-end pass, which drives the real app and needs one stable hook that
 * survives copy edits.
 */

const TYPE_BY_STATE: Record<string, 'warning' | 'error' | 'info'> = {
  blocked: 'warning',
  overridden: 'warning',
  caution: 'warning',
  unrecorded: 'warning',
};

const TAG_COLOR_BY_STATE: Record<string, string> = {
  blocked: 'red',
  overridden: 'gold',
  caution: 'orange',
  unrecorded: 'default',
};

/** The failing checks, each as its own sentence with a readable name. */
const CheckSentences: React.FC<{ checks: AnswerKeyCheck[] }> = ({ checks }) => {
  if (!checks.length) return null;
  return (
    <ul
      className="answer-key-gate-checks"
      style={{ margin: '0 0 8px', paddingLeft: 18 }}
    >
      {checks.slice(0, 4).map(check => (
        <li key={check.key}>
          <Typography.Text strong>{checkLabel(check.key)}</Typography.Text>
          {check.key ? (
            <Typography.Text type="secondary"> ({check.key})</Typography.Text>
          ) : null}
          {check.detail ? <div>{check.detail}</div> : null}
        </li>
      ))}
    </ul>
  );
};

/**
 * The retirement note (D8). It is never a warning about the score: the key was
 * fit when it graded, and nothing is withheld. It is here because the reader
 * will go looking for this key on the blueprint page and find its replacement.
 *
 * The backend narrates the retirement itself when it has one to narrate
 * (`with_live_key_state` replaces a retired key's headline), so the sentence
 * here would be the same fact in a second voice. Where that has happened, only
 * the part the headline cannot carry is added: the key that took its place.
 */
const retiredLine = (gate: AnswerKeyGate): string => {
  if (!gate.retiredNote) return '';
  if (!/retire/i.test(gate.headline)) return gate.retiredNote;
  return gate.supersededBy
    ? `It was replaced by ${gate.supersededBy}, which grades different content.`
    : '';
};

const RetiredNote: React.FC<{ note: string }> = ({ note }) =>
  note ? (
    <Typography.Paragraph
      className="answer-key-gate-retired"
      style={{ marginBottom: 4 }}
    >
      {note}
    </Typography.Paragraph>
  ) : null;

/** The instruments the candidate was scored on and could not see. */
const InvisibleInstruments: React.FC<{ problems: string[] }> = ({
  problems,
}) => {
  if (!problems.length) return null;
  return (
    <ul
      className="answer-key-gate-invisible"
      style={{ margin: '0 0 8px', paddingLeft: 18 }}
    >
      {problems.map(problem => (
        <li key={problem}>{problem}</li>
      ))}
    </ul>
  );
};

const describe = (gate: AnswerKeyGate): React.ReactNode => {
  const meta = [
    gate.code ? `Answer key ${gate.code}` : '',
    gate.computedAt
      ? `checked ${new Date(gate.computedAt).toLocaleString()}`
      : '',
    gate.gateVersion ? `gate ${gate.gateVersion}` : '',
    gate.passBarSentence,
  ]
    .filter(Boolean)
    .join(' · ');

  if (gate.state === 'overridden') {
    return (
      <>
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          {gate.overriddenAt
            ? `Approved on ${new Date(gate.overriddenAt).toLocaleString()}.`
            : 'Approved for grading.'}{' '}
          {gate.overrideReason
            ? `Reason given: “${gate.overrideReason}”`
            : null}
        </Typography.Paragraph>
        <CheckSentences checks={gate.failingChecks} />
        <RetiredNote note={retiredLine(gate)} />
        <InvisibleInstruments problems={gate.invisibleInstruments} />
        {meta ? (
          <Typography.Text type="secondary">{meta}</Typography.Text>
        ) : null}
      </>
    );
  }

  if (gate.state === 'unrecorded') {
    return (
      <>
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          {gate.headline || UNRECORDED_CONSEQUENCE}
        </Typography.Paragraph>
        <RetiredNote note={retiredLine(gate)} />
        <InvisibleInstruments problems={gate.invisibleInstruments} />
        {meta ? (
          <Typography.Text type="secondary">{meta}</Typography.Text>
        ) : null}
      </>
    );
  }

  return (
    <>
      {gate.headline && (
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          {gate.headline}
        </Typography.Paragraph>
      )}
      <CheckSentences checks={gate.failingChecks} />
      {gate.state === 'blocked' && (!gate.headline || gate.stale) && (
        // A stale failure gets the consequence sentence even when the backend
        // authored a headline: that headline was written about the version the
        // check graded, and it reads as a statement about this one.
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          {gateConsequence(gate)}
        </Typography.Paragraph>
      )}
      {gate.stale && (
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          {STALE_NOTE}
        </Typography.Paragraph>
      )}
      <RetiredNote note={retiredLine(gate)} />
      <InvisibleInstruments problems={gate.invisibleInstruments} />
      {meta ? <Typography.Text type="secondary">{meta}</Typography.Text> : null}
    </>
  );
};

export type VerdictBannerProps = {
  /** `readAnswerKeyGate(...)` output. `null` renders nothing. */
  gate: AnswerKeyGate | null;
  /**
   * `full` is the banner (a report, a results screen). `compact` is the same
   * verdict as a tag with the sentence on hover, for a picker row or a summary
   * card where a paragraph would drown the thing being chosen.
   */
  variant?: 'full' | 'compact';
  className?: string;
};

const VerdictBanner: React.FC<VerdictBannerProps> = ({
  gate,
  variant = 'full',
  className,
}) => {
  if (!gate) return null;
  // A PASSING answer key with a scored instrument the candidate could not see
  // still has something to say, and so does one that has since been retired.
  // Everything else clear renders nothing: the banner has to stay rare to stay
  // read.
  if (
    gate.state === 'clear' &&
    !gate.invisibleInstruments.length &&
    !gate.retiredNote
  ) {
    return null;
  }

  if (variant === 'compact') {
    const detail = gate.failingChecks.find(check => check.detail)?.detail;
    const hover =
      gate.state === 'overridden'
        ? `${gateHeadline(gate)}${gate.overrideReason ? `: “${gate.overrideReason}”` : ''}`
        : gate.state === 'unrecorded'
          ? UNRECORDED_CONSEQUENCE
          : [detail, gateConsequence(gate)].filter(Boolean).join(' ');
    const hoverWithVisibility = [
      hover,
      gate.invisibleInstruments[0],
      gate.retiredNote,
    ]
      .filter(Boolean)
      .join(' ');
    // A retired key whose audit passed has nothing to warn about, so the tag
    // names the one fact that is true: it is out of service.
    const label =
      gate.state === 'clear' && gate.retiredNote
        ? RETIRED_TAG
        : gateShortLabel(gate);
    return (
      <span
        data-answer-key-gate={gate.state}
        data-answer-key-retired={gate.retired ? 'true' : undefined}
        className={className}
      >
        <Tooltip title={hoverWithVisibility}>
          <Tag color={TAG_COLOR_BY_STATE[gate.state] || 'default'}>{label}</Tag>
        </Tooltip>
      </span>
    );
  }

  // A clear audit on a retired key is information, not a warning, and it is
  // typed as such: colouring provenance orange is how a screen teaches people
  // that orange means nothing.
  const clearAndRetired = gate.state === 'clear' && Boolean(gate.retiredNote);
  const headline =
    gateHeadline(gate) ||
    (gate.invisibleInstruments.length
      ? INVISIBLE_INSTRUMENTS_HEADLINE
      : RETIRED_HEADLINE);
  return (
    <div
      data-answer-key-gate={gate.state}
      data-answer-key-retired={gate.retired ? 'true' : undefined}
      className={className}
    >
      <Alert
        className="assessment-callout answer-key-gate-banner"
        type={clearAndRetired ? 'info' : TYPE_BY_STATE[gate.state] || 'warning'}
        showIcon
        title={headline}
        description={describe(gate)}
      />
    </div>
  );
};

/**
 * The rating band, or the sentence that replaces it. Used everywhere a rating
 * is printed beside a score, so the suppression cannot be implemented in one
 * screen and forgotten in the next.
 */
/** What a trainer, client or trainee reads in place of a rating band the
 *  answer key cannot support. Administrators read UNRATED_LABEL and why. */
export const NOT_RATED_LABEL = 'Not rated';

export const AnswerKeyRating: React.FC<{
  rating?: string | null;
  gate: AnswerKeyGate | null;
  /** Render as an antd Tag (tables) rather than plain text. */
  asTag?: boolean;
  color?: string;
  /** Assessment maintenance details belong to administrators. */
  showDiagnostics?: boolean;
}> = ({ rating, gate, asTag, color, showDiagnostics = true }) => {
  if (gate?.suppressRating) {
    if (!showDiagnostics) {
      // An outcome, not a diagnosis, and not a promise: this read "Results
      // pending review" until 2026-10-09, but nothing is queued behind a
      // failed audit, so a client waited on a review that never came.
      return asTag ? (
        <Tag color="default">{NOT_RATED_LABEL}</Tag>
      ) : (
        <Typography.Text type="secondary">{NOT_RATED_LABEL}</Typography.Text>
      );
    }
    return (
      <Tooltip title={gateConsequence(gate) || BLOCKED_CONSEQUENCE}>
        {asTag ? (
          <Tag color="default">{UNRATED_LABEL}</Tag>
        ) : (
          <Typography.Text type="secondary">{UNRATED_LABEL}</Typography.Text>
        )}
      </Tooltip>
    );
  }
  const label = rating || '—';
  return asTag ? <Tag color={color}>{label}</Tag> : <span>{label}</span>;
};

/** The rating-band legend, withheld when the answer key cannot support it. */
export const RatingLegend: React.FC<{
  gate: AnswerKeyGate | null;
  text: string;
}> = ({ gate, text }) => {
  if (gate?.suppressLegend) return null;
  return <span>{text}</span>;
};

/** A launchable scenario's audit state, for a summary card or a picker row. */
export const AnswerKeyGateTag: React.FC<{ gate: AnswerKeyGate | null }> = ({
  gate,
}) => {
  if (!gate) return null;
  if (gate.state === 'clear' && !gate.retiredNote) {
    return (
      <Space size={4}>
        <Tag color="green">Quality audit passed</Tag>
      </Space>
    );
  }
  return <VerdictBanner gate={gate} variant="compact" />;
};

export default VerdictBanner;
